/**
 * KAN-755: состояние формы скидки: загрузка типов и карточки (при правке),
 * значения полей, смена типа (параметры сбрасываются под новый тип),
 * клиентская проверка, сохранение и ошибки 400 по полям (включая params.*).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDiscount, useDiscountMutations, useDiscountTypes } from '@/hooks/useDiscounts';
import {
  discountToForm,
  emptyDiscountForm,
  emptyParams,
  serverDiscountErrors,
  validateDiscountForm,
  type DiscountFormValues,
  type FormErrors,
  type ParamValue,
  type ScopeValues,
} from '@/lib/discountForm';
import type { DiscountDetail, DiscountType } from '@/types/discount';

export interface DiscountFormState {
  values: DiscountFormValues;
  types: DiscountType[];
  type: DiscountType | undefined;
  errors: FormErrors;
  formError: string | null;
  /** Типы или карточка скидки ещё грузятся. */
  loading: boolean;
  loadError: boolean;
  pending: boolean;
  setName: (name: string) => void;
  setFormula: (formula: string) => void;
  setParam: (name: string, value: ParamValue) => void;
  setActive: (isActive: boolean) => void;
  setScope: (patch: Partial<ScopeValues>) => void;
  submit: () => Promise<DiscountDetail | null>;
}

export function useDiscountForm(open: boolean, discountId: string | null): DiscountFormState {
  const typesQuery = useDiscountTypes();
  const detailQuery = useDiscount(open ? discountId : null);
  const { create, update } = useDiscountMutations();
  const types = useMemo(() => typesQuery.data ?? [], [typesQuery.data]);

  const [values, setValues] = useState<DiscountFormValues>(emptyDiscountForm());
  const [errors, setErrors] = useState<FormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  // Форма заполняется один раз на открытие: повторная загрузка не затирает ввод.
  const initialized = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      initialized.current = null;
      return;
    }
    const key = discountId ?? 'new';
    if (initialized.current === key || !typesQuery.data) return;
    if (discountId) {
      if (!detailQuery.data) return;
      const detail = detailQuery.data;
      setValues(discountToForm(detail, typesQuery.data.find((t) => t.code === detail.formula)));
    } else {
      const first = typesQuery.data[0];
      setValues({ ...emptyDiscountForm(first?.code ?? ''), params: emptyParams(first) });
    }
    setErrors({});
    setFormError(null);
    initialized.current = key;
  }, [open, discountId, typesQuery.data, detailQuery.data]);

  const type = types.find((t) => t.code === values.formula);
  const clearError = (key: string): void =>
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const submit = async (): Promise<DiscountDetail | null> => {
    const result = validateDiscountForm(values, type);
    setErrors(result.errors);
    setFormError(null);
    if (!result.body) return null;
    try {
      return discountId
        ? await update.mutateAsync({ id: discountId, body: result.body })
        : await create.mutateAsync(result.body);
    } catch (err) {
      const server = serverDiscountErrors(err);
      setErrors(server.fields);
      setFormError(server.form);
      return null;
    }
  };

  return {
    values,
    types,
    type,
    errors,
    formError,
    loading: typesQuery.isLoading || (!!discountId && detailQuery.isLoading),
    loadError: typesQuery.isError || (!!discountId && detailQuery.isError),
    pending: create.isPending || update.isPending,
    setName: (name) => {
      clearError('name');
      setValues((prev) => ({ ...prev, name }));
    },
    setFormula: (formula) => {
      const next = types.find((t) => t.code === formula);
      setErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith('params'))));
      setValues((prev) => ({ ...prev, formula, params: emptyParams(next) }));
    },
    setParam: (name, value) => {
      clearError(`params.${name}`);
      setValues((prev) => ({ ...prev, params: { ...prev.params, [name]: value } }));
    },
    setActive: (isActive) => setValues((prev) => ({ ...prev, isActive })),
    setScope: (patch) => {
      clearError('scope');
      setValues((prev) => ({ ...prev, ...patch }));
    },
    submit,
  };
}
